import { useEffect } from "react";

/**
 * Hook to ensure browser back/forward navigation works properly
 * Handles keyboard shortcuts (Cmd+Left/Right, Alt+Left/Right) and ensures
 * touchpad gestures work correctly
 *
 * Note: React Router should handle popstate events automatically, but this
 * ensures keyboard shortcuts work and that nothing is blocking navigation.
 * Touchpad gestures work through horizontal wheel events which the browser
 * converts to navigation - we ensure these events can propagate.
 */
export const useBrowserNavigation = () => {
    useEffect(() => {
        // Handle keyboard shortcuts for browser navigation
        // These shortcuts are typically handled by the browser, but we ensure
        // they work even if something is preventing default behavior
        const handleKeyDown = (e: KeyboardEvent) => {
            // Check for browser navigation shortcuts
            // macOS: Cmd+Left/Right, Windows/Linux: Alt+Left/Right
            const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
            const modifierKey = isMac ? e.metaKey : e.altKey;

            if (
                modifierKey &&
                (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Left" || e.key === "Right")
            ) {
                // Don't prevent default - let the browser handle it naturally
                // React Router will handle the popstate event that follows
                // This ensures the navigation works even if something else is
                // trying to prevent it
            }
        };

        // Ensure popstate events are not being blocked
        // React Router handles these automatically, but we verify the event
        // can propagate
        const handlePopState = (_event: PopStateEvent) => {
            // React Router should handle this automatically via its Router component
            // We just ensure the event isn't being stopped
            // The actual navigation is handled by React Router
        };

        // Ensure wheel events (used for touchpad gestures) can propagate
        // Touchpad swipe gestures generate horizontal wheel events which the browser
        // converts to navigation. We ensure these events aren't being blocked.
        const handleWheel = (e: WheelEvent) => {
            // Only care about horizontal wheel events (touchpad swipe gestures)
            // Don't prevent default - let the browser handle navigation gestures
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                // This is a horizontal scroll - could be a navigation gesture
                // Let the browser handle it naturally
            }
        };

        const keydownOptions: AddEventListenerOptions = { passive: true };
        const popstateOptions: AddEventListenerOptions = { passive: true };
        const wheelOptions: AddEventListenerOptions = { passive: true, capture: true };

        window.addEventListener("keydown", handleKeyDown, keydownOptions);
        window.addEventListener("popstate", handlePopState, popstateOptions);
        // Use capture phase to ensure we see the event before anything else can block it
        window.addEventListener("wheel", handleWheel, wheelOptions);

        return () => {
            window.removeEventListener("keydown", handleKeyDown, keydownOptions);
            window.removeEventListener("popstate", handlePopState, popstateOptions);
            window.removeEventListener("wheel", handleWheel, wheelOptions);
        };
    }, []);
};
